import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from 'react-router-dom';
import {lazy, Suspense} from 'react';
import {AppLayout} from '@widgets/app-shell/ui/AppLayout';
import {PageSkeleton} from '@shared/ui/primitives/PageSkeleton';
import {RequireAuth} from './RequireAuth';
import {ModuleGuard} from './ModuleGuard';
import {WorkspaceActionGuard} from './WorkspaceActionGuard';
import {RouteErrorPage} from './RouteErrorPage';

const DashboardPage = lazy(() =>
  import('@pages/dashboard-page').then((m) => ({default: m.DashboardPage})),
);
const CallsPage = lazy(() =>
  import('@pages/calls-page').then((m) => ({default: m.CallsPage})),
);
const MeetingsPage = lazy(() =>
  import('@pages/meetings-page').then((m) => ({default: m.MeetingsPage})),
);
// Заглушка выключённого модуля — отдельным импортом по файлу, а не через barrel
// страницы: иначе ради статичного экрана грузился бы весь код ленты встреч.
const MeetingsDisabledPage = lazy(() =>
  import('@pages/meetings-page/ui/MeetingsDisabledPage').then((m) => ({
    default: m.MeetingsDisabledPage,
  })),
);
const ClientsPage = lazy(() =>
  import('@pages/clients-page').then((m) => ({default: m.ClientsPage})),
);
const ClientCardPage = lazy(() =>
  import('@pages/client-card-page').then((m) => ({
    default: m.ClientCardPage,
  })),
);
const TasksPage = lazy(() =>
  import('@pages/tasks-page').then((m) => ({default: m.TasksPage})),
);
const ProcessedPage = lazy(() =>
  import('@pages/processed-page').then((m) => ({default: m.ProcessedPage})),
);
const TeamPage = lazy(() =>
  import('@pages/team-page').then((m) => ({default: m.TeamPage})),
);
// Метрики сотрудников: админ/оператор — полный набор, комдир/РОП — своя ветка.
const UserMetricsPage = lazy(() =>
  import('@pages/user-metrics-page').then((m) => ({
    default: m.UserMetricsPage,
  })),
);
// Управление воркспейсом: песочница (планы + коммуникации, manage-plans/оператор)
// и настройки (manage-workspace).
const SandboxPage = lazy(() =>
  import('@pages/sandbox-page').then((m) => ({default: m.SandboxPage})),
);
const SandboxPlansTab = lazy(() =>
  import('@pages/sandbox-page').then((m) => ({default: m.PlansTab})),
);
const SandboxCommunicationsTab = lazy(() =>
  import('@pages/sandbox-page').then((m) => ({default: m.CommunicationsTab})),
);
const AllCommunicationsPage = lazy(() =>
  import('@pages/all-communications-page').then((m) => ({
    default: m.AllCommunicationsPage,
  })),
);
const WorkspaceSettingsPage = lazy(() =>
  import('@pages/workspace-settings-page').then((m) => ({
    default: m.WorkspaceSettingsPage,
  })),
);
// Администрирование сервиса (только операторы платформы).
const AdminClientsPage = lazy(() =>
  import('@pages/admin-clients-page').then((m) => ({
    default: m.AdminClientsPage,
  })),
);
const AdminSourcesPage = lazy(() =>
  import('@pages/admin-sources-page').then((m) => ({
    default: m.AdminSourcesPage,
  })),
);
const AdminMonitoringPage = lazy(() =>
  import('@pages/admin-monitoring-page').then((m) => ({
    default: m.AdminMonitoringPage,
  })),
);
const LoginPage = lazy(() =>
  import('@pages/login-page').then((m) => ({default: m.LoginPage})),
);
const SupportPage = lazy(() =>
  import('@pages/support-page').then((m) => ({default: m.SupportPage})),
);
const PrivacyPolicyPage = lazy(() =>
  import('@pages/privacy-policy').then((m) => ({default: m.PrivacyPolicyPage})),
);
const CookiePolicyPage = lazy(() =>
  import('@pages/cookie-policy').then((m) => ({default: m.CookiePolicyPage})),
);
const NotFoundPage = lazy(() =>
  import('@pages/not-found-page').then((m) => ({default: m.NotFoundPage})),
);

const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<PageSkeleton />}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  // /login лежит вне AppLayout — на нём нет sidebar/topbar.
  // Защита остальных роутов от неавторизованных делается в AuthProvider
  // (редирект на /login при отсутствии сохранённого юзера / 401 от /me).
  {path: '/login', element: withSuspense(LoginPage)},
  // Публичные страницы (доступны без авторизации — со страницы входа).
  // Их список продублирован в `./public-routes.ts` для AuthProvider —
  // при добавлении нового публичного роута обновить оба места.
  {path: '/support', element: withSuspense(SupportPage)},
  {path: '/privacy-policy', element: withSuspense(PrivacyPolicyPage)},
  {path: '/cookie-policy', element: withSuspense(CookiePolicyPage)},
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      {index: true, element: <Navigate to='/dashboard' replace />},
      // ── Рабочее пространство ────────────────────────────────────────────
      {path: 'dashboard', element: withSuspense(DashboardPage)},
      {path: 'calls', element: withSuspense(CallsPage)},
      // Раздел живёт под модулем «Встречи». Модуль выключен — РОП, коммерческий
      // директор и админ воркспейса видят заглушку «ещё не подключён», а
      // менеджера (личный кабинет) guard уводит на дашборд: раздела
      // для него нет ни в меню, ни по прямой ссылке.
      {
        path: 'meetings',
        element: (
          <ModuleGuard
            module='meetings'
            fallback={
              <WorkspaceActionGuard action='view-communications'>
                {withSuspense(MeetingsDisabledPage)}
              </WorkspaceActionGuard>
            }
          >
            {withSuspense(MeetingsPage)}
          </ModuleGuard>
        ),
      },
      // Клиенты: менеджеру — только свои (скоуп на бэке), остальным — по
      // view-communications. Карточка открывается и из деталки звонка/встречи.
      {
        path: 'clients',
        element: (
          <WorkspaceActionGuard action='view-communications' allowPersonal>
            {withSuspense(ClientsPage)}
          </WorkspaceActionGuard>
        ),
      },
      {
        path: 'clients/:clientSid',
        element: (
          <WorkspaceActionGuard action='view-communications' allowPersonal>
            {withSuspense(ClientCardPage)}
          </WorkspaceActionGuard>
        ),
      },
      {path: 'tasks', element: withSuspense(TasksPage)},
      {
        path: 'processed',
        element: (
          <WorkspaceActionGuard action='view-communications' allowPersonal>
            {withSuspense(ProcessedPage)}
          </WorkspaceActionGuard>
        ),
      },
      // ── Управление воркспейсом ──────────────────────────────────────────
      {path: 'team', element: withSuspense(TeamPage)},
      // Метрики: view-analytics есть у админа, комдира и РОПа (у менеджера — нет).
      // Не `/metrics`: этот путь шлюз (Gatekeeper, любой bcf-сервис) отдаёт сам —
      // Prometheus-экспорт, и до фронта прямой заход/F5 не доходит.
      {
        path: 'user-metrics',
        element: (
          <WorkspaceActionGuard action='view-analytics'>
            {withSuspense(UserMetricsPage)}
          </WorkspaceActionGuard>
        ),
      },
      // Песочница: две вкладки — планы анализа и коммуникации. Доступна
      // администратору воркспейса (manage-plans) и оператору платформы.
      {
        path: 'sandbox',
        element: withSuspense(SandboxPage),
        children: [
          {index: true, element: <Navigate to='/sandbox/plans' replace />},
          {path: 'plans', element: withSuspense(SandboxPlansTab)},
          {
            path: 'communications',
            element: withSuspense(SandboxCommunicationsTab),
          },
        ],
      },
      // Диагностика приёма: все коммуникации воркспейса + техническая инфа.
      // Доступ — manage-workspace или оператор (проверка внутри страницы).
      {path: 'communications', element: withSuspense(AllCommunicationsPage)},
      {
        path: 'settings/workspace',
        element: withSuspense(WorkspaceSettingsPage),
      },
      // Легаси-адреса: редактор планов раньше жил отдельной страницей `/plans`,
      // в «настройках анализа» и в админ-песочнице; песочница коммуникаций —
      // в блоке оператора. Всё это теперь вкладки единой «Песочницы».
      {path: 'plans', element: <Navigate to='/sandbox/plans' replace />},
      {
        path: 'settings/analysis-plan',
        element: <Navigate to='/sandbox/plans' replace />,
      },
      // ── Администрирование сервиса (операторы платформы) ─────────────────
      {path: 'admin', element: <Navigate to='/admin/monitoring' replace />},
      {
        path: 'admin/plans',
        element: <Navigate to='/sandbox/plans' replace />,
      },
      {
        path: 'admin/sandbox',
        element: <Navigate to='/sandbox/communications' replace />,
      },
      {path: 'admin/clients', element: withSuspense(AdminClientsPage)},
      {path: 'admin/sources', element: withSuspense(AdminSourcesPage)},
      {path: 'admin/monitoring', element: withSuspense(AdminMonitoringPage)},
    ],
  },
  // 404 — любой неизвестный маршрут.
  {path: '*', element: withSuspense(NotFoundPage)},
];

export const router = createBrowserRouter(
  // Корневая граница ошибок: падение любой страницы (в т.ч. не загрузившийся
  // чанк после выкладки) показывает RouteErrorPage, а не экран React Router.
  [{errorElement: <RouteErrorPage />, children: routes}],
  {
    // На GitHub Pages приложение живёт на /colver-rop/ — Vite подставляет
    // BASE_URL автоматически из vite.config.ts. На локалке остаётся '/'.
    basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/',
  },
);
